/**
 * Página de Reportes — resumen consolidado del consultorio del doctor.
 *
 * KPIs: Pacientes, Consultas atendidas, Recetas, Ingresos totales.
 * Charts: Consultas por semana (8), Consultas por mes (6) + overlay ingresos.
 * Rankings: Enfermedades más atendidas, Top pacientes por ingresos.
 *
 * Todos los datos vienen de endpoints reales (/patients, /appointments,
 * /prescriptions, /diagnoses, /payments) y se agregan en cliente.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { ArrowUpRight, Users, CalendarClock, Pill, DollarSign, Activity } from "@/components/icons";

const startOfWeek = (d) => {
  const nd = new Date(d);
  const day = nd.getDay();
  const diff = (day + 6) % 7;
  nd.setDate(nd.getDate() - diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const startOfMonth = (d) => {
  const nd = new Date(d);
  nd.setDate(1);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const fmtWeek = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthName = (m) =>
  ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"][m];
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DoctorReports() {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a, r, d, pay] = await Promise.all([
          api.get("/patients").catch(() => ({ data: [] })),
          api.get("/appointments").catch(() => ({ data: [] })),
          api.get("/prescriptions").catch(() => ({ data: [] })),
          api.get("/diagnoses").catch(() => ({ data: [] })),
          api.get("/payments").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setPatients(p.data || []);
        setAppointments(a.data || []);
        setPrescriptions(r.data || []);
        setDiagnoses(d.data || []);
        setPayments(pay.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => {
    const completed = appointments.filter((a) => a.status === "completed").length;
    const income = payments
      .filter((p) => (p.status || "paid") === "paid")
      .reduce((s, p) => s + Number(p.amount || 0), 0);
    return {
      patients: patients.length,
      completed,
      appointments: appointments.length,
      prescriptions: prescriptions.length,
      diagnoses: diagnoses.length,
      income,
    };
  }, [patients, appointments, prescriptions, diagnoses, payments]);

  const weeklyAppts = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 7; i >= 0; i--) {
      const anchor = new Date(now);
      anchor.setDate(anchor.getDate() - i * 7);
      const start = startOfWeek(anchor);
      buckets.push({ label: fmtWeek(start), start: start.getTime(), count: 0 });
    }
    appointments.forEach((a) => {
      const iso = a.starts_at || a.date;
      if (!iso) return;
      const t = new Date(iso);
      if (Number.isNaN(t.getTime())) return;
      const s = startOfWeek(t).getTime();
      const bucket = buckets.find((b) => b.start === s);
      if (bucket) bucket.count += 1;
    });
    return buckets;
  }, [appointments]);

  const monthlyStats = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        label: `${monthName(d.getMonth())} ${String(d.getFullYear()).slice(-2)}`,
        start: startOfMonth(d).getTime(),
        count: 0,
        income: 0,
      });
    }
    appointments.forEach((a) => {
      const iso = a.starts_at || a.date;
      if (!iso) return;
      const t = new Date(iso);
      if (Number.isNaN(t.getTime())) return;
      const s = startOfMonth(t).getTime();
      const bucket = buckets.find((b) => b.start === s);
      if (bucket) bucket.count += 1;
    });
    payments.forEach((p) => {
      if ((p.status || "paid") !== "paid") return;
      const iso = p.paid_at || p.created_at;
      if (!iso) return;
      const t = new Date(iso);
      if (Number.isNaN(t.getTime())) return;
      const s = startOfMonth(t).getTime();
      const bucket = buckets.find((b) => b.start === s);
      if (bucket) bucket.income += Number(p.amount || 0);
    });
    return buckets;
  }, [appointments, payments]);

  const topDx = useMemo(() => {
    const map = new Map();
    diagnoses.forEach((d) => {
      const key = (d.cie10 || d.cie11 || d.diagnosis || "").toString().trim();
      if (!key) return;
      const label = d.diagnosis
        ? `${d.diagnosis}${d.cie10 ? " · " + d.cie10 : ""}`
        : key;
      const entry = map.get(key) || { name: label, value: 0 };
      entry.value += 1;
      map.set(key, entry);
    });
    return Array.from(map.values())
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [diagnoses]);

  const topPatients = useMemo(() => {
    const nameById = Object.fromEntries(patients.map((p) => [p.id, p.full_name]));
    const map = new Map();
    payments.forEach((p) => {
      if ((p.status || "paid") !== "paid" || !p.patient_id) return;
      const entry = map.get(p.patient_id) || {
        id: p.patient_id,
        name: nameById[p.patient_id] || p.patient_name || "Paciente",
        amount: 0,
        count: 0,
      };
      entry.amount += Number(p.amount || 0);
      entry.count += 1;
      map.set(p.patient_id, entry);
    });
    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
  }, [payments, patients]);

  const totalWeekly = weeklyAppts.reduce((n, w) => n + w.count, 0);
  const totalMonthlyCount = monthlyStats.reduce((n, m) => n + m.count, 0);
  const totalMonthlyIncome = monthlyStats.reduce((n, m) => n + m.income, 0);

  return (
    <div className="space-y-3 pb-4" data-testid="doctor-reports-page">
      {/* Header */}
      <div>
        <div className="text-[13px] font-semibold text-black tracking-tight">
          Reportes del consultorio
        </div>
        <div className="text-[11px] text-black/45 mt-0.5">
          Resumen consolidado de tu actividad clínica y financiera
        </div>
      </div>

      {/* KPIs globales */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} label="Pacientes" value={totals.patients} sub="En tu cartera" testid="report-patients-total" />
        <Kpi icon={Activity} label="Consultas atendidas" value={totals.completed} sub={`${totals.appointments} programadas`} testid="report-completed-total" />
        <Kpi icon={Pill} label="Recetas" value={totals.prescriptions} sub="Emitidas en total" testid="report-prescriptions-total" />
        <Kpi icon={DollarSign} label="Ingresos" value={fmtMoney(totals.income)} sub="Cobrado acumulado" testid="report-income-total" numeric={false} />
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-black/[0.05] p-8 text-center text-[13px] text-black/45">
          Cargando reportes…
        </div>
      ) : (
        <>
          {/* Consultas + Ingresos por mes */}
          <ChartCard
            title="Consultas e ingresos por mes"
            subtitle="Últimos 6 meses"
            trailing={
              <div className="flex items-center gap-3 text-[11px]">
                <span className="inline-flex items-center gap-1.5 text-black/60">
                  <span className="w-2 h-2 rounded-full bg-black" /> {totalMonthlyCount} consultas
                </span>
                <span className="inline-flex items-center gap-1.5 text-black/60">
                  <span className="w-2 h-2 rounded-full" style={{ background: "#8FE388" }} /> {fmtMoney(totalMonthlyIncome)}
                </span>
              </div>
            }
            testid="report-monthly-chart"
          >
            <MonthChart data={monthlyStats} />
          </ChartCard>

          {/* Consultas por semana */}
          <ChartCard
            title="Citas por semana"
            subtitle="Últimas 8 semanas (lunes a domingo)"
            trailing={
              <span className="text-[11px] font-semibold text-black/60">{totalWeekly} totales</span>
            }
            testid="report-weekly-chart"
          >
            <WeekChart data={weeklyAppts} />
          </ChartCard>

          {/* Rankings */}
          <div className="grid lg:grid-cols-2 gap-3">
            <ChartCard title="Enfermedades más atendidas" subtitle="Ranking por diagnóstico (CIE-10 cuando existe)" testid="report-top-dx">
              {topDx.length === 0 ? (
                <div className="text-[13px] text-black/45">Aún no hay diagnósticos registrados.</div>
              ) : (
                <RankList items={topDx} format={(v) => v} />
              )}
            </ChartCard>

            <ChartCard title="Top pacientes por ingresos" subtitle="Cobros acumulados por paciente" testid="report-top-patients">
              {topPatients.length === 0 ? (
                <div className="text-[13px] text-black/45">Aún no hay cobros registrados.</div>
              ) : (
                <RankList
                  items={topPatients.map((p) => ({ name: p.name, value: p.amount, sub: `${p.count} cobro${p.count === 1 ? "" : "s"}` }))}
                  format={fmtMoney}
                />
              )}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

// ── UI ────────────────────────────────────────────────────────────────

function Kpi({ icon: Icon, label, value, sub, testid, numeric = true }) {
  return (
    <div className="bg-white rounded-xl border border-black/[0.05] p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[13px] font-semibold text-black">{label}</div>
        <span className="w-7 h-7 rounded-md bg-[#F2F3F5] flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-black/70" strokeWidth={1.75} weight="regular" />
        </span>
      </div>
      <div className={`text-[24px] font-semibold text-black leading-none ${numeric ? "tabular-nums" : ""}`}>
        {value}
      </div>
      <div className="text-[11px] text-black/45 mt-1.5">{sub}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, trailing, testid, children }) {
  return (
    <div className="bg-white rounded-xl border border-black/[0.05] p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-black">{title}</div>
          {subtitle && <div className="text-[11px] text-black/45 mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          {trailing}
          <span className="w-7 h-7 rounded-md bg-[#F2F3F5] flex items-center justify-center flex-shrink-0">
            <ArrowUpRight className="w-3.5 h-3.5 text-black/70" strokeWidth={1.75} weight="regular" />
          </span>
        </div>
      </div>
      {children}
    </div>
  );
}

function WeekChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end gap-2 h-32 pt-2">
      {data.map((d, i) => {
        const h = (d.count / max) * 100;
        const isLast = i === data.length - 1;
        return (
          <div key={d.start} className="flex-1 flex flex-col items-center gap-1.5">
            <div
              className="w-full rounded-md transition"
              style={{ height: `${Math.max(6, h)}%`, background: isLast ? "#000" : "#F2F3F5" }}
              title={`${d.count} citas`}
            />
            <div className="text-[10px] font-semibold text-black/40 uppercase tracking-widest">
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthChart({ data }) {
  const maxCount = Math.max(1, ...data.map((d) => d.count));
  const maxIncome = Math.max(1, ...data.map((d) => d.income));
  return (
    <div className="flex items-end gap-3 h-40 pt-2">
      {data.map((d) => {
        const hc = (d.count / maxCount) * 100;
        const hi = (d.income / maxIncome) * 100;
        return (
          <div key={d.start} className="flex-1 flex flex-col items-center gap-1.5">
            <div className="w-full h-full flex items-end gap-1">
              <div
                className="flex-1 rounded-md bg-black transition"
                style={{ height: `${Math.max(4, hc)}%` }}
                title={`${d.count} consultas`}
              />
              <div
                className="flex-1 rounded-md transition"
                style={{ height: `${Math.max(4, hi)}%`, background: "#8FE388" }}
                title={fmtMoney(d.income)}
              />
            </div>
            <div className="text-[10px] font-semibold text-black/40 uppercase tracking-widest">
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RankList({ items, format }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="space-y-2.5">
      {items.map((it, i) => {
        const pct = Math.round((it.value / max) * 100);
        return (
          <div key={`${it.name}-${i}`} data-testid={`report-rank-${i}`}>
            <div className="flex items-baseline justify-between mb-1 gap-3">
              <div className="text-[13px] font-semibold text-black truncate flex-1">
                <span className="text-black/40 font-mono mr-1.5 text-[11px]">{String(i + 1).padStart(2, "0")}</span>
                {it.name}
                {it.sub && (
                  <span className="text-[11px] text-black/40 ml-2 font-normal">· {it.sub}</span>
                )}
              </div>
              <div className="text-[13px] font-semibold text-black tabular-nums">
                {format(it.value)}
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-[#F2F3F5] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, background: "#8FE388" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
